//@flow
import React from 'react';
import times from 'lodash/times';
import { Box } from 'gestalt';

const HALF_STAR =
  'M12.00,0.00 L12.00,0.00 C11.40,0.00 10.79,0.35 10.57,1.06 L8.48,7.92 L1.49,7.92 C0.07,7.92 -0.54,9.75 0.59,10.62 L6.30,15.03 L4.16,22.04 C3.73,23.44 5.34,24.57 6.49,23.68 L12.00,19.43 L12.00,0.00 Z';
const FULL_STAR =
  'M13.43,1.06 L15.52,7.92 L22.51,7.92 C23.93,7.92 24.54,9.75 23.41,10.62 L17.70,15.03 L19.84,22.04 C20.27,23.44 18.66,24.57 17.51,23.68 L12.00,19.43 L6.49,23.68 C5.34,24.57 3.73,23.44 4.16,22.04 L6.30,15.03 L0.59,10.62 C-0.54,9.75 0.07,7.92 1.49,7.92 L8.48,7.92 L10.58,1.06 C11.01,-0.35 12.99,-0.35 13.43,1.06';
const HALF_STAR_RIGHT =
  'M12.00,0.00 L12.00,0.00 C12.60,0.00 13.21,0.35 13.43,1.06 L15.52,7.92 L22.51,7.92 C23.93,7.92 24.55,9.75 23.41,10.62 L17.70,15.03 L19.84,22.04 C20.27,23.44 18.66,24.57 17.51,23.68 L12.00,19.43 L12.00,0.00 Z';

type Props = {
  max_rating?: string,
  rating: string,
  width: number,
  color?: 'gray' | 'yellow',
  isLego?: boolean,
};

type PinRatingStarProps = {
  fill: 'half' | 'empty' | 'full',
  width: number,
  color?: 'gray' | 'yellow',
  isLego: boolean,
};

const PinRatingStar = ({ fill, width, color, isLego = false }: PinRatingStarProps) => {
  const fullStarColor = color === 'yellow' ? '#e2780d' : '#333';
  const starElement =
    fill === 'half' ? (
      <svg
        height={width}
        width={width}
        viewBox="0 0 24 24"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path fill={fullStarColor} d={HALF_STAR} />
        <path fill="lightgray" d={HALF_STAR_RIGHT} />
      </svg>
    ) : (
      <svg
        height={width}
        width={width}
        viewBox="0 0 24 24"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path fill={fill === 'full' ? fullStarColor : 'lightgray'} d={FULL_STAR} />
      </svg>
    );

  return (
    <Box
      dangerouslySetInlineStyle={{ __style: { marginRight: isLego ? '3px' : 0 } }}
      display="inlineBlock"
    >
      {starElement}
    </Box>
  );
};
//returns a rating scaled out of 5 (ex: 60/100 = 3/5)
function scaleRating(max_rating: number, rating: number) {
  //if the rating is more than the max_rating we set it equal to the max rating
  rating = rating <= max_rating ? rating : max_rating;
  return (rating * 5) / max_rating;
}
export default function RatingStars({ max_rating, rating, width, color, isLego = false }: Props) {
  const icons = [];
  //if theres no max_rating we assume its out of 5
  const trueRating = scaleRating(parseFloat(max_rating) || 5, parseFloat(rating) || 0);

  if (isNaN(trueRating)) {
    return null;
  }

  const fullStars = Math.floor(trueRating);
  const partialStars = trueRating - fullStars;

  times(fullStars, (idx) =>
    icons.push(<PinRatingStar fill="full" width={width} key={idx} color={color} isLego={isLego} />),
  );

  if (partialStars >= 0.75) {
    icons.push(
      <PinRatingStar fill="full" width={width} key={icons.length} color={color} isLego={isLego} />,
    );
  } else if (partialStars >= 0.25) {
    icons.push(
      <PinRatingStar fill="half" width={width} key={icons.length} color={color} isLego={isLego} />,
    );
  }

  times(5 - icons.length, () =>
    icons.push(
      <PinRatingStar fill="empty" width={width} key={icons.length} color={color} isLego={isLego} />,
    ),
  );

  return <React.Fragment>{icons}</React.Fragment>;
}
